import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import UapiCustomCodeAttachments from './shared/UapiCustomCodeAttachments';
import './PartnerSyncTemplates.css';
import './IntegrationRouteCodePage.css';
import './UniversalApiPage.css';

// THE CUSTOM CODE A PIPELINE TEMPLATE RUNS AROUND ONE ROUTE'S INTEGRATION (V533) — opened from the Custom Code
// button in the designer's Integration column, the counterpart of the proxy's button beside it.
//
// The proxy's code (IntegrationRouteCodePage) rewrites what the source sends to the Universal API. This is
// the other side of the hub: Universal API custom code (V528) that runs before and after the call the
// route's TARGET integration makes — the Azure BLOB file name, say. Attached here, it runs for every
// property implementing the template, on the backend binding each chose for this route, ahead of anything
// attached to that binding itself.
//
// A LIST, NOT A SECOND EDITOR. The snippets are ordinary Universal API custom code; each opens in that
// editor (with Claude and the hook tester), told it came from this route so Back returns here. The list is
// the shared one the Universal API binding page uses.
//
// A FULL SCREEN, NOT A MODAL (STYLE.md), like the proxy's.

const IntegrationRouteUapiCodePage = () => {
    const { id, routeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // The editor's own query comes back with it — `from` and `mcpSource` decide where ITS Back goes.
    const backPath = `/pipeline-templates/${id}/edit${location.search || ''}`;

    const [route, setRoute] = useState(null);
    const [error, setError] = useState(null);

    const onLoaded = useCallback((d) => { if (d && d.target) setRoute(d.target); }, []);

    // A route drawn with no target system cannot say what its code would run around.
    const noSystem = route && !route.marketplace_id;

    return (
        <div className="pst-container">
            <div className="pst-detail-header pst-detail-header-stacked">
                <button className="pst-back-btn" onClick={() => navigate(backPath)}>
                    ← Back to {route ? route.template_name : 'the pipeline template'}
                </button>
                <div className="pst-detail-header-main">
                    <div>
                        <div className="pst-detail-title">
                            Integration custom code{route && route.route_name ? ` — ${route.route_name}` : ''}
                        </div>
                        <div className="irc-route">
                            <span className="irc-hop">Universal API</span>
                            <span className="irc-arrow" aria-hidden="true">→</span>
                            <span className="irc-mods"><code>before()</code></span>
                            <span className="irc-arrow" aria-hidden="true">→</span>
                            <span className="irc-hop">{route && route.system_name ? route.system_name : 'target'} integration</span>
                            <span className="irc-arrow" aria-hidden="true">→</span>
                            <span className="irc-mods"><code>after()</code></span>
                        </div>
                    </div>
                </div>
            </div>

            {error && <div className="irc-empty">{error}</div>}
            {noSystem && (
                <div className="irc-empty">
                    This route has no target system yet. Choose one on the design first — custom code is written for
                    one system&rsquo;s integration.
                </div>
            )}

            <UapiCustomCodeAttachments
                target={{ kind: 'route', id: routeId }}
                systemName={route && route.system_name}
                intro={<>
                    Runs on this route&rsquo;s calls to {route && route.system_name ? route.system_name : 'the target'} for
                    every property implementing this template — only in the modules each one targets — in this
                    order, ahead of any custom code a property attaches to its own backend:{' '}
                    <code>before()</code> ahead of the call, <code>after()</code> once it answers.
                </>}
                emptyText={`No custom code — ${route && route.system_name ? route.system_name : 'the target'} is `
                    + 'called exactly as its integration does, unless a property attaches its own.'}
                detachTitle={(c) => `Stop running ${c.name} on this route — the code itself is kept`}
                onOpen={(codeId) => navigate(`/universal-api/custom-code/${codeId}?routeId=${routeId}`)}
                onNew={() => navigate(`/universal-api/custom-code/new?routeId=${routeId}`)}
                onLoaded={onLoaded}
                onError={setError}
                disabled={!!noSystem}
            />
        </div>
    );
};

export default IntegrationRouteUapiCodePage;
